import type { IScope } from "angular";
import { finalize } from "rxjs/operators";
import { cloneDeep, isEmpty, noop } from "lodash";
import "@progress/kendo-ui/js/kendo.autocomplete";

import type { ILabel, IPartnerUserFlat, IResponseWithLoading, IRole } from "App/Modules/Models";
import { RoleFunctions, RolesGroup, RolesGroupFunctions } from "App/Modules/Models";
import type { IPartnerUsersRepository, IRolesRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { PatchDelta } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class EditPartnerUserComponentController {
    public static $inject: string[] = ["$scope", "$translate", "mmPartnerUsersRepository", "irRolesRepository"];

    public edited: IPartnerUserFlat;

    public original: IPartnerUserFlat;

    public accessCode: string;

    public editResponse: IResponseWithLoading<IPartnerUserFlat>;

    public deleteResponse: IResponseWithLoading<void>;

    public partnerRoles: IRole[];

    public partnerRolesResponse: IResponseWithLoading<IRole[]>;

    public isPartnerRolesEditable: boolean;

    public labels: string[];

    public partnerUserLabelsString: string;

    public withLimitedPermissions: boolean;

    public partnerUser: IPartnerUserFlat;

    public resolve: IFormContainerResolve<
        IPartnerUserFlat,
        { partnerUser: IPartnerUserFlat; withLimitedPermissions: boolean }
    >;

    constructor(
        private readonly $scope: IScope,
        private readonly $translate: ng.translate.ITranslateService,
        private readonly partnerUsersRepository: IPartnerUsersRepository,
        private readonly rolesRepository: IRolesRepository,
    ) {}

    public $onInit() {
        this.original = this.resolve.formData;
        this.withLimitedPermissions = this.resolve.bindings.withLimitedPermissions;
        this.partnerUser = this.resolve.bindings.partnerUser;
        this.accessCode = "";

        this.prepareRoles();

        this.edited = cloneDeep(this.original);
        this.partnerUserLabelsString =
            this.original.labels === null ? "" : this.original.labels.map((l) => l.label).join(",");

        extendWithLoadingAndFail(this.partnerUsersRepository.getLabels(this.original.partner.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    if (response.isSuccessful) {
                        this.labels = response.data.map((l) => l.label);
                    }
                },
                () => noop,
            );
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public sortById(role: IRole) {
        return RoleFunctions.sortById(role);
    }

    public save() {
        const patchObject = PatchDelta.difference(this.original, this.edited);
        if (patchObject.role) {
            patchObject.role = patchObject.role.id === null ? null : patchObject.role;
        }

        if (this.original.labels === null) {
            if (this.partnerUserLabelsString !== "") {
                this.splitLabels(patchObject);
            }
        } else if (this.original.labels.map((l) => l.label).join(",") !== this.partnerUserLabelsString) {
            this.splitLabels(patchObject);
        }

        if (this.accessCode && this.accessCode !== "") {
            patchObject.accessCode = this.accessCode.toUpperCase();
        } else if (this.accessCode === null) {
            patchObject.accessCode = null;
        }

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(
                this.partnerUsersRepository.patchPartnerUser(
                    this.partnerUser.partner.id,
                    this.partnerUser.id,
                    patchObject,
                ),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (editResponse: IResponseWithLoading<IPartnerUserFlat>) => {
                        this.editResponse = editResponse;
                        if (!editResponse.isLoading && editResponse.data) {
                            this.resolve.options.onOk(editResponse.data);
                        }
                    },
                    (errorResponse) => {
                        this.editResponse = errorResponse;
                    },
                );
        } else {
            this.cancel();
        }
    }

    public delete() {
        extendWithLoadingAndFail(
            this.partnerUsersRepository.deletePartnerUser(this.partnerUser.partner.id, this.partnerUser.id),
        ).subscribe(
            (deleteResponse: IResponseWithLoading<void>) => {
                this.deleteResponse = deleteResponse;

                if (!deleteResponse.isLoading) {
                    this.resolve.options.onOk(null);
                }
            },
            (error) => {
                this.deleteResponse = error;
            },
        );
    }

    public deleteAccessCode() {
        this.accessCode = null;
        this.edited.hasAccessCode = false;
    }

    public addAccessCode() {
        this.edited.hasAccessCode = true;
    }

    private splitLabels(patchObject: IPartnerUserFlat) {
        patchObject.labels = this.partnerUserLabelsString
            .split(",")
            .map((l) => l.trim())
            .filter((l) => l !== "")
            .map((l) => ({ label: l } as ILabel));
    }

    private prepareRoles() {
        if (this.original.role === null) {
            this.original.role = this.rolesRepository.getNoRole();
            this.isPartnerRolesEditable =
                this.original.connectedToPartner !== null ? this.original.connectedToPartner : false;
        } else {
            this.original.role = this.rolesRepository.addTranslatedLabel(this.original.role);
            this.isPartnerRolesEditable =
                RolesGroupFunctions.RolesGroup(this.original.role) === RolesGroup.Partner &&
                this.original.connectedToPartner !== null
                    ? this.original.connectedToPartner
                    : false;
        }

        if (this.isPartnerRolesEditable) {
            const rolesObservable = this.withLimitedPermissions
                ? this.rolesRepository.getRolesForSysAdmin()
                : this.rolesRepository.getPartnerRoles();
            extendWithLoadingAndFail(rolesObservable)
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response: IResponseWithLoading<IRole[]>) => {
                        this.partnerRolesResponse = response;
                        if (!response.hasError && response.data) {
                            this.partnerRoles = this.withLimitedPermissions
                                ? response.data
                                : [this.rolesRepository.getNoRole()].concat(response.data);
                        }
                    },
                    (error) => {
                        this.partnerRolesResponse = error;
                    },
                );
        }
    }
}
